import React, { useEffect, useMemo, useState } from "react";
import Header from "../../../components/Header/Header";
import Tabs from "../../../components/Tabs/Tabs";
import mes_tab_data from "../../../assets/mockedData/mes_tab_data";
import { savePathToLocalStorage } from "../../../utils/helpers";
import mesApi from "../../../Api/mes.api";
import {
  Ingredient,
  IngredientAlias,
  GoodsReceipt,
  Warehouse as WarehouseType,
  WAREHOUSE_TYPE_MATERIAL,
} from "../../../types/mes";
import { ToastContainer, toast } from "react-toastify";
import GoodsReceiptWizard from "./components/GoodsReceiptWizard";
import SearchableSelect, {
  SearchableSelectOption,
} from "../../../components/DataEntry/SearchableSelect";
import MesPagination, { usePagedList } from "../../../components/MES/MesPagination";
import ReceiptImageThumb from "./components/ReceiptImageThumb";
import { t, dateLocale } from "../../../i18n/locale";

const RECEIPTS_PAGE_SIZE = 15;

const ReceiveDelivery = () => {
  savePathToLocalStorage();

  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [ingredientAliases, setIngredientAliases] = useState<IngredientAlias[]>([]);
  const [receipts, setReceipts] = useState<GoodsReceipt[]>([]);
  const [loading, setLoading] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [showSingleModal, setShowSingleModal] = useState(false);
  const receiptsPager = usePagedList(receipts, RECEIPTS_PAGE_SIZE);

  const [warehouses, setWarehouses] = useState<WarehouseType[]>([]);
  const [newLot, setNewLot] = useState({
    ingredient_id: 0,
    ritam_alias_id: 0,
    supplier: "",
    lot_number: "",
    delivery_date: new Date().toISOString().split("T")[0],
    expiry_date: "",
    quantity_kg: "",
    price_per_kg: "",
    notes: "",
    warehouse_id: 0,
  });

  useEffect(() => {
    loadIngredients();
    loadIngredientAliases();
    loadReceipts();
    loadWarehouses();
  }, []);

  /** Only material warehouses may hold raw stock, so only those are offered. */
  const loadWarehouses = async () => {
    try {
      const list = await mesApi.getSkladista({ type: WAREHOUSE_TYPE_MATERIAL });
      setWarehouses(list);
      setNewLot((prev) => ({
        ...prev,
        warehouse_id:
          prev.warehouse_id || list.find((w) => w.is_default_receipt)?.warehouse_id || 0,
      }));
    } catch (error: any) {
      toast.error(t("Učitavanje skladišta: {message}", { message: error.message }));
    }
  };

  const loadIngredients = async () => {
    try {
      const data = await mesApi.getIngredients({ is_active: true });
      setIngredients(data);
    } catch (error: any) {
      toast.error(t("Učitavanje sirovina: {message}", { message: error.message }));
    }
  };

  const loadIngredientAliases = async () => {
    try {
      const data = await mesApi.getIngredientAliases({ source_system: "ritam" });
      setIngredientAliases(data);
    } catch (error: any) {
      toast.error(t("Učitavanje Ritam sirovina: {message}", { message: error.message }));
    }
  };

  const loadReceipts = async () => {
    try {
      setLoading(true);
      const data = await mesApi.getGoodsReceipts();
      setReceipts(data || []);
    } catch (error: any) {
      toast.error(t("Učitavanje dostava: {message}", { message: error.message }));
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleCreateSingleLot = async () => {
    if (!newLot.ritam_alias_id || !newLot.quantity_kg) {
      toast.error(t("Sirovina (Ritam) i količina su obavezni"));
      return;
    }
    if (!newLot.warehouse_id) {
      toast.error(t("Odaberite skladište na koje ide zaliha"));
      return;
    }

    try {
      setLoading(true);
      await mesApi.createIngredientLot({
        ...newLot,
        quantity_kg: parseFloat(newLot.quantity_kg),
        remaining_kg: parseFloat(newLot.quantity_kg),
        price_per_kg: newLot.price_per_kg ? parseFloat(newLot.price_per_kg) : undefined,
      });
      toast.success(t("Dostava registrirana — stavka je u karanteni i čeka odobrenje."));
      setShowSingleModal(false);
      resetSingleForm();
      loadReceipts();
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const resetSingleForm = () => {
    setNewLot({
      ingredient_id: 0,
      ritam_alias_id: 0,
      supplier: "",
      lot_number: "",
      delivery_date: new Date().toISOString().split("T")[0],
      expiry_date: "",
      quantity_kg: "",
      price_per_kg: "",
      notes: "",
      // Keep the warehouse: entering several lots in a row almost always means
      // the same delivery arriving at the same place.
      warehouse_id: newLot.warehouse_id,
    });
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString(dateLocale());
  };

  const statusLabel = (status: string) => {
    if (status === "quarantine") return { label: t("Karantena"), class: "badge-quarantine" };
    if (status === "released") return { label: t("Pušten"), class: "stock-good" };
    return { label: status, class: "" };
  };

  const ingredientOptions: SearchableSelectOption[] = useMemo(
    () =>
      ingredients.map((ing) => ({
        value: ing.ID,
        label: ing.IngredientName,
        sublabel: ing.StandardCode || ing.RitamCode || undefined,
      })),
    [ingredients]
  );

  const aliasOptions: SearchableSelectOption[] = useMemo(
    () =>
      ingredientAliases.map((a) => ({
        value: a.ID,
        label: a.AliasName,
        sublabel: a.ExternalCode || undefined,
      })),
    [ingredientAliases]
  );

  const handleSelectRitamAlias = (aliasId: number) => {
    const alias = ingredientAliases.find((a) => a.ID === aliasId);
    setNewLot((prev) => ({
      ...prev,
      ritam_alias_id: aliasId,
      ingredient_id: alias?.Ingredient ? alias.Ingredient : prev.ingredient_id,
    }));
  };

  return (
    <div className="mes-page">
      <Header title={t("MES - Skladiste")} />
      <div className="main-content-flex">
        <Tabs data={mes_tab_data} />
        <div className="section mes-content">
          <ToastContainer position="top-center" autoClose={3000} style={{ zIndex: 10050 }} />

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", flexWrap: "wrap", gap: "10px" }}>
            <h2>{t("Primke")}</h2>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              <button type="button" className="btn-secondary" onClick={() => setShowSingleModal(true)}>
                {t("+ Jedna stavka")}
              </button>
              <button type="button" className="btn-primary" onClick={() => setShowWizard(true)}>
                {t("+ Grupna dostava")}
              </button>
            </div>
          </div>

          <div className="mes-section">
            <h3 style={{ marginBottom: "15px" }}>{t("Povijest primki")}</h3>
            {loading && receipts.length === 0 ? (
              <p style={{ textAlign: "center", padding: "20px" }}>{t("Učitavanje...")}</p>
            ) : receipts.length === 0 ? (
              <p style={{ textAlign: "center", padding: "40px" }}>{t("Nema registriranih dostava")}</p>
            ) : (
              <>
              <div className="mes-table-wrap">
                <table className="mes-table delivery-receipts-table">
                <thead>
                  <tr>
                    <th style={{ width: 40 }}></th>
                    <th>{t("Broj otpremnice")}</th>
                    <th>{t("Datum")}</th>
                    <th>{t("Dobavljač")}</th>
                    <th>{t("Skladište")}</th>
                    <th>{t("Stavki")}</th>
                    <th title={t("Broj stavki koje čekaju odobrenje")}>{t("Karantena")}</th>
                    <th>{t("Dokument")}</th>
                  </tr>
                </thead>
                <tbody>
                  {receiptsPager.pageItems.map((r) => {
                    const rowId = r.delivery_receipt_id;
                    const expanded = expandedIds.has(rowId);
                    return (
                      <React.Fragment key={rowId}>
                        <tr
                          className="delivery-receipt-row"
                          onClick={() => toggleExpand(rowId)}
                          style={{ cursor: "pointer" }}
                        >
                          <td>{expanded ? "▼" : "▶"}</td>
                          <td><strong>{r.delivery_note_number}</strong></td>
                          <td>{formatDate(r.received_date)}</td>
                          <td>{r.supplier_name || "-"}</td>
                          <td>{r.warehouse_name || "—"}</td>
                          <td>{r.lots?.length || 0}</td>
                          <td>
                            {r.quarantine_count > 0 ? (
                              <span className="badge badge-quarantine">{r.quarantine_count}</span>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td onClick={(e) => e.stopPropagation()}>
                            {r.delivery_note_image_url || (r.lots || []).some((lot) => lot.lot_label_image_url) ? (
                              <ReceiptImageThumb
                                images={[
                                  ...(r.delivery_note_image_url
                                    ? [{ url: r.delivery_note_image_url, label: t("Otpremnica") }]
                                    : []),
                                  ...((r.lots || [])
                                    .filter((lot) => lot.lot_label_image_url)
                                    .map((lot) => ({
                                      url: lot.lot_label_image_url as string,
                                      label: lot.lot_number ? t("Lot {number}", { number: lot.lot_number }) : t("Lot"),
                                    }))),
                                ]}
                              />
                            ) : (
                              "-"
                            )}
                          </td>
                        </tr>
                        {expanded && (
                          <tr className="delivery-receipt-detail-row">
                            <td colSpan={8} style={{ padding: 0 }}>
                              <table className="mes-table nested-lots-table">
                                <thead>
                                  <tr>
                                    <th>{t("Sirovina (Ritam)")}</th>
                                    <th>{t("Sirovina")}</th>
                                    <th>{t("LOT")}</th>
                                    <th>{t("Kg")}</th>
                                    <th>{t("Preostalo")}</th>
                                    <th>{t("Status")}</th>
                                    <th>{t("Rok")}</th>
                                    <th></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(r.lots || []).map((lot) => {
                                    const st = statusLabel(lot.status);
                                    return (
                                      <tr key={lot.lot_id}>
                                        <td>{lot.ritam_alias_name || "-"}</td>
                                        <td>{lot.zboogie_ingredients?.IngredientName || "-"}</td>
                                        <td><strong>{lot.lot_number || "-"}</strong></td>
                                        <td style={{ fontFamily: "monospace" }}>{lot.quantity_kg?.toFixed(2)}</td>
                                        <td style={{ fontFamily: "monospace" }}>{lot.remaining_kg?.toFixed(2)}</td>
                                        <td><span className={`badge ${st.class}`}>{st.label}</span></td>
                                        <td>{formatDate(lot.expiry_date)}</td>
                                        <td>
                                          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                            {lot.status === "quarantine" && (
                                              <span className="mes-hint" style={{ fontSize: "0.85em" }}>
                                                {t("Odobravanje u Kvaliteti →")}
                                              </span>
                                            )}
                                            {lot.lot_label_image_url && (
                                              <ReceiptImageThumb
                                                images={[{ url: lot.lot_label_image_url, label: lot.lot_number ? t("Lot {number}", { number: lot.lot_number }) : t("Lot") }]}
                                              />
                                            )}
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
              </div>
              <MesPagination
                pager={receiptsPager}
                itemLabel={t("primki")}
                className="delivery-receipts-pagination"
              />
              </>
            )}
          </div>
        </div>
      </div>

      {showWizard && (
        <GoodsReceiptWizard
          ingredients={ingredients}
          ingredientAliases={ingredientAliases}
          onClose={() => setShowWizard(false)}
          onSuccess={loadReceipts}
        />
      )}

      {showSingleModal && (
        <div className="mes-modal-overlay" onClick={() => setShowSingleModal(false)}>
          <div className="mes-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t("Jedna stavka")}</h3>
              <button type="button" className="close-btn" onClick={() => setShowSingleModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>{t("Sirovina (Ritam) *")}</label>
                <SearchableSelect
                  value={newLot.ritam_alias_id}
                  options={aliasOptions}
                  onChange={(val) => handleSelectRitamAlias(val)}
                  placeholder={t("Pretraži Ritam sirovine...")}
                />
              </div>
              <div className="form-group">
                <label>{t("Sirovina")}</label>
                <SearchableSelect
                  value={newLot.ingredient_id}
                  options={ingredientOptions}
                  onChange={(val) => setNewLot({ ...newLot, ingredient_id: val })}
                  placeholder={t("Pretraži sirovine...")}
                />
              </div>
              <div className="form-grid-2">
                <div className="form-group">
                  <label>{t("LOT")}</label>
                  <input type="text" value={newLot.lot_number} onChange={(e) => setNewLot({ ...newLot, lot_number: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>{t("Dobavljač")}</label>
                  <input type="text" value={newLot.supplier} onChange={(e) => setNewLot({ ...newLot, supplier: e.target.value })} />
                </div>
              </div>
              <div className="form-grid-2">
                <div className="form-group">
                  <label>{t("Datum *")}</label>
                  <input type="date" value={newLot.delivery_date} onChange={(e) => setNewLot({ ...newLot, delivery_date: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>{t("Količina (kg) *")}</label>
                  <input type="number" step="0.01" value={newLot.quantity_kg} onChange={(e) => setNewLot({ ...newLot, quantity_kg: e.target.value })} />
                </div>
              </div>
              <div className="form-group">
                <label>{t("Skladište *")}</label>
                <select
                  value={newLot.warehouse_id || ""}
                  onChange={(e) => setNewLot({ ...newLot, warehouse_id: Number(e.target.value) })}
                >
                  <option value="">{t("-- Odaberi skladište --")}</option>
                  {warehouses.map((w) => (
                    <option key={w.warehouse_id} value={w.warehouse_id}>
                      {w.code} · {w.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-cancel" onClick={() => setShowSingleModal(false)}>{t("Odustani")}</button>
              <button type="button" className="btn-save" onClick={handleCreateSingleLot} disabled={loading}>{t("Spremi")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReceiveDelivery;
